import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

/**
 * View-and-set card for the CO element's raw voltage readout (SETTINGS v5+).
 *
 * The sensor publishes co_ppm from a curve fitted against one element, so a
 * field report that "the CO number looks wrong" cannot be checked without the
 * voltage the number was computed from. Turning this on adds a co_v column to
 * the recorded CSV and a CO Voltage tile to the sensor's page.
 *
 * The flag is sensor state, not per-viewer: once it is on, everyone with access
 * to that sensor sees the tile.
 */
export default function CoVoltageCard({ serialClient, onError = undefined }) {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(false);
  const [selected, setSelected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const enabled = await serialClient.getCoVoltageDebug();
        if (cancelled) return;
        setCurrent(enabled);
        setSelected(enabled);
      } catch {
        // Leave the switch at off and usable rather than blocking the card on
        // the read — the control is what fixes a sensor that answered badly.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialClient]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setSuccess(null);
    setError(null);
    try {
      await serialClient.setCoVoltageDebug(selected);
      setCurrent(selected);
      setSuccess(
        selected
          ? 'The sensor is now reporting its CO element voltage.'
          : 'The sensor has stopped reporting its CO element voltage.',
      );
    } catch (err) {
      const message = err.message || 'Failed to save the CO voltage readout';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, onError, selected]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          CO sensor voltage readout
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Adds the CO element&apos;s raw voltage to the sensor&apos;s recorded CSV and to its
          readings. Diagnostic: the ppm reading is computed from this voltage through a curve
          calibrated on one sensor, so the voltage is the measurement that can be trusted.
        </Typography>

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <FormControlLabel
              sx={{ mb: 2, display: 'block' }}
              control={
                <Switch
                  checked={selected}
                  onChange={(e) => setSelected(e.target.checked)}
                  inputProps={{ 'aria-label': 'Report CO sensor voltage' }}
                />
              }
              label="Report CO sensor voltage"
            />

            <Button
              variant="contained"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={busy || selected === current}
            >
              {busy ? 'Saving...' : 'Save'}
            </Button>
          </>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

CoVoltageCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
